import {
  BellOutlined,
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  TableOutlined,
} from '@ant-design/icons'
import {
  Badge,
  Button,
  Input,
  message,
  Modal,
  Popover,
  Table,
  Tag,
  Tabs,
  Checkbox,
  Spin,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import {
  antdTableHorizontalScroll,
  stickyActionsColumn,
  visibleHorizontalScroll,
} from 'src/components/styled/horizontalScroll'
import { setCatalogueFilter } from 'src/features/FilterSlice'
import ShareWithGroupButton from 'src/screens/menu-builder/components/ShareWithGroupButton'
import { API } from 'src/utils/interceptor'
import {
  fetchAllSuppliers,
  fetchOMSSuppliers,
} from 'src/features/SupplierSlice'
import {
  editVenueSupplier,
  getVenueSuppliers,
  requestPriceRefresh,
  switchVenueSupplierPriceFile,
} from 'src/features/VenueSuppliersSlice'
import EditVenueSupplierModal from 'src/screens/venue-suppliers/EditVenueSupplierModal'
import ReportPriceFileIssueButton from 'src/components/productIssue/ReportPriceFileIssueButton'
import SupplierProgress from 'src/components/growthEngine/SupplierProgress'
import GrowthEngineFeatureEmptyState from 'src/components/growthEngine/GrowthEngineFeatureEmptyState'
import GrowthEngineFirstSuccessCompletion from 'src/components/growthEngine/GrowthEngineFirstSuccessCompletion'
import RestrictSupplierFirstTrySheet from 'src/components/growthEngine/RestrictSupplierFirstTrySheet'
import useGrowthEngineFeatureGuidance from 'src/hooks/useGrowthEngineFeatureGuidance'
import useGrowthEngineSuggestedData from 'src/hooks/useGrowthEngineSuggestedData'
import useJustAchievedFirstSuccess from 'src/hooks/useJustAchievedFirstSuccess'
import { shouldHideFirstTryMarketingBanners } from 'src/utils/growthEngineOneJobPage'
import {
  getFirstSuccessCompletion,
  shouldShowFirstSuccessCompletion,
  shouldShowRestrictedSuppliersGuidedEmpty,
} from 'src/utils/growthEngineRecommendation'
import {
  bindGuidedEmptySuggestedActions,
  pickAcceptedSuggestedItem,
} from 'src/utils/growthEngineSuggestedData'
import {
  buildFirstTryRestrictSavePayload,
  defaultFirstTryApprovalEmails,
  pickExistingSupplierForFirstRestrict,
} from 'src/utils/growthEngineRestrictedSuppliers'
import { omitInternalOrFixtureSuggestedItems } from 'src/utils/growthEngineInternalTitle'
import AddSupplierHub from 'src/screens/venue-suppliers/AddSupplierHub'
import ConnectSupplierWizard from 'src/screens/venue-suppliers/connect-wizard/ConnectSupplierWizard'
import InviteSupplierModal from 'src/screens/venue-suppliers/InviteSupplierModal'
import { countGrowthEngineActiveSuppliers } from 'src/utils/supplierLadderProgress'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'
import {
  filterVenueSuppliersVisibleColumns,
  listVenueSuppliersOptionalColumns,
  loadVenueSuppliersHiddenColumns,
  saveVenueSuppliersHiddenColumns,
  toggleVenueSuppliersHiddenColumn,
  VENUE_SUPPLIERS_OPTIONAL_COLUMN_DEFS,
} from 'src/utils/venueSuppliersColumns'
import useLunrSearch from 'src/utils/useLunrSearch'
import useUserRole from 'src/hooks/useUserRole'
import useIsBlocked, { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import {
  VENUE_ROLE_CAPABILITY,
  hasCapability,
} from 'src/utils/venueRoleCapabilities'
import { notifyRestrictedSupplierPaywallSales } from 'src/utils/restrictedSupplierPaywallNotify'
import PlanLimitModal from 'src/screens/subscriptions/PlanLimitModal'
import styled, { css } from 'styled-components/macro'
import {
  getSupplierInvites,
  removeSupplierInvite,
  formatInviteDate,
} from 'src/utils/supplierInvitesStorage'
import { showErrorToast, showSuccessToast } from 'src/utils/messageToast'
import { formatDeliveryDays } from 'src/utils/deliveryDays'
import { supplierSaveConfirmation } from 'src/utils/venueSupplierDraft'
import {
  ORDER_CUTOFF_INSTRUCTIONS_LABEL,
  ORDER_CUTOFF_TIME_LABEL,
  resolveOrderCutOffInstructionsDisplay,
} from 'src/utils/supplierOrderCutOff'

const { Search } = Input

const venueSuppliersTabKey = venueId => `oms:venueSuppliersTab:${venueId}`

const readStoredSuppliersTab = (venueId, canManageSuppliers) => {
  if (!canManageSuppliers) return 'existing'
  if (!venueId) return 'connect'
  try {
    const stored = localStorage.getItem(venueSuppliersTabKey(venueId))
    if (stored === 'existing' || stored === 'connect') return stored
  } catch {
    // ignore storage errors
  }
  return 'connect'
}

const VenueSuppliers = props => {
  const { venueId } = props.match.params
  const dispatch = useDispatch()
  const userRole = useUserRole()
  const customRoles = useVenueCustomRoles()
  const venue = useSelector(state => state.venue.venue)
  const isVenueLoaded = Boolean(venue?._id)
  const canManageSuppliers =
    isVenueLoaded &&
    hasCapability(userRole, VENUE_ROLE_CAPABILITY.SUPPLIERS, customRoles)
  const productIssuesBlocked = useIsBlocked('product-issues')
  const canReportPriceFileIssue = !productIssuesBlocked
  const account = useSelector(state => state.auth.account)
  const suppliers = useSelector(state => state.suppliers.suppliers)
  const venueSuppliers = useSelector(
    state => state.venueSuppliers.venueSuppliers,
  )
  const supplierConnectionCount = useSelector(
    state => state.venueSuppliers.supplierConnectionCount,
  )
  const isLoading = useSelector(state => state.venueSuppliers.isLoading)
  const accountCreditApproved = useSelector(
    state => state.auth.account.creditApproved,
  )
  const isSomeVenueSupplierActive = useSelector(
    state => state.venueSuppliers.isSomeVenueSupplierActive,
  )
  const usageLimits = useSelector(state => state.venue?.venue?.usageLimits)
  const currentPlan = usageLimits?.currentPlan
  const growthEngineActiveSupplierCount = useMemo(
    () => countGrowthEngineActiveSuppliers(venueSuppliers),
    [venueSuppliers],
  )
  const isAdmin = account && account.isAdmin
  const isTfrAdmin = account && account.TFRAdmin
  const isJustAdmin = account && account.JUSTAdmin
  const isGreenAdmin = account && account.GREENAdmin
  const [searchTerm, setSearchTerm] = useState('')
  const [columnPickerOpen, setColumnPickerOpen] = useState(false)
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState(() =>
    loadVenueSuppliersHiddenColumns(venueId),
  )
  const [searchVenueSuppliers, setSearchVenueSuppliers] = useState([])
  const [editingVenueSupplier, setEditingVenueSupplier] = useState(null)
  const [editingSupplierSaving, setEditingSupplierSaving] = useState(false)
  const heldEditingVenueSupplierRef = useRef(null)
  const mappingAtOpenRef = useRef(null)
  if (editingVenueSupplier) {
    heldEditingVenueSupplierRef.current = editingVenueSupplier
  }
  useEffect(() => {
    if (!editingVenueSupplier) {
      mappingAtOpenRef.current = null
      return
    }
    if (mappingAtOpenRef.current?.id === editingVenueSupplier._id) return
    mappingAtOpenRef.current = {
      id: editingVenueSupplier._id,
      defaultAccount: editingVenueSupplier.defaultAccount || '',
      defaultContact: editingVenueSupplier.defaultContact || '',
    }
  }, [editingVenueSupplier])
  // Keep last supplier so Edit does not hollow to Cancel/OK while closing.
  const supplierModalEntity =
    editingVenueSupplier || heldEditingVenueSupplierRef.current
  const [firstTryRestrictSupplier, setFirstTryRestrictSupplier] = useState(null)
  const [firstTryRestricted, setFirstTryRestricted] = useState(true)
  const [firstTryApprovalEmails, setFirstTryApprovalEmails] = useState([])
  const [firstTrySaving, setFirstTrySaving] = useState(false)
  const [activeTab, setActiveTab] = useState(() =>
    readStoredSuppliersTab(venueId, canManageSuppliers),
  )
  const [showConnectWizard, setShowConnectWizard] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteSupplierName, setInviteSupplierName] = useState('')
  const [preConnectSupplierCount, setPreConnectSupplierCount] = useState(0)
  const [pendingInvites, setPendingInvites] = useState([])
  const [completingInviteId, setCompletingInviteId] = useState(null)
  const [showRestrictedLimitModal, setShowRestrictedLimitModal] =
    useState(false)
  const guidedSuccess = useGrowthEngineFeatureGuidance({
    venueId,
    surface: 'restricted_suppliers',
    featureId: 'restricted_suppliers',
  })
  const suggested = useGrowthEngineSuggestedData({
    venueId,
    featureId: 'restricted_suppliers',
    items: guidedSuccess?.suggestedData?.items,
  })
  const showRestrictedGuidedEmpty = shouldShowRestrictedSuppliersGuidedEmpty({
    venueSuppliers,
    guidedSuccess,
    loading: isLoading,
  })
  const hideFirstTryMarketing = shouldHideFirstTryMarketingBanners(
    showRestrictedGuidedEmpty,
  )
  const firstSuccessCompletion = getFirstSuccessCompletion(guidedSuccess)
  const hasRestricted = (venueSuppliers || []).some(
    supplier => supplier?.restricted === true,
  )
  const [justAchievedFirstRestrict, setJustAchievedFirstRestrict] =
    useJustAchievedFirstSuccess({
      loading: isLoading,
      isEmpty: !hasRestricted,
      hasOutcome: Boolean(firstSuccessCompletion?.outcome),
    })
  const showFirstSuccessCompletion = shouldShowFirstSuccessCompletion({
    justAchieved: justAchievedFirstRestrict,
    guidedSuccess,
  })
  const linkPrefix = venueId ? `/v/${venueId}/catalogue` : '/admin/orders'
  const history = useHistory()

  const showRestrictedUpgradeModal = useCallback(
    (supplierContext = null) => {
      notifyRestrictedSupplierPaywallSales({
        venueId,
        source: 'client',
        platform: 'dashboard',
        supplierTitle: supplierContext?.supplierTitle ?? null,
      })

      setShowRestrictedLimitModal(true)
    },
    [venueId],
  )

  const [sharedPriceFileIds, setSharedPriceFileIds] = useState(() => new Set())

  useEffect(() => {
    if (suppliers.length === 0) dispatch(fetchAllSuppliers())
    if (venueSuppliers.length === 0 && venueId)
      dispatch(getVenueSuppliers(venueId))
  }, [dispatch, venueId])

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    API.get(
      'api2',
      `venue-groups/source-search?kind=priceFile&venueId=${venueId}&all=1`,
    )
      .then(response => {
        if (cancelled) return null
        setSharedPriceFileIds(
          new Set((response?.data || []).map(row => String(row._id || ''))),
        )
        return null
      })
      .catch(() => {
        if (!cancelled) setSharedPriceFileIds(new Set())
        return null
      })
    return () => {
      cancelled = true
    }
  }, [venueId])

  useEffect(() => {
    if (activeTab === 'existing' && venueId) {
      dispatch(getVenueSuppliers(venueId))
    }
  }, [activeTab, venueId, dispatch])

  useEffect(() => {
    if (canManageSuppliers && activeTab === 'connect') {
      dispatch(fetchOMSSuppliers())
    }
  }, [activeTab, dispatch, canManageSuppliers])

  useEffect(() => {
    setActiveTab(readStoredSuppliersTab(venueId, canManageSuppliers))
  }, [venueId, canManageSuppliers])

  useEffect(() => {
    setHiddenColumnKeys(loadVenueSuppliersHiddenColumns(venueId))
  }, [venueId])

  const showAdminSupplierColumns =
    Boolean(isAdmin) ||
    Boolean(isTfrAdmin) ||
    Boolean(isJustAdmin) ||
    Boolean(isGreenAdmin)
  const suppliersPickerRows = listVenueSuppliersOptionalColumns(
    VENUE_SUPPLIERS_OPTIONAL_COLUMN_DEFS,
    {
      showAdmin: showAdminSupplierColumns,
      showCredit: Boolean(accountCreditApproved && isAdmin),
    },
  )
  const pickerHiddenKeys = Array.isArray(hiddenColumnKeys)
    ? hiddenColumnKeys
    : []
  const handleToggleSuppliersColumn = columnKey => {
    const next = toggleVenueSuppliersHiddenColumn(pickerHiddenKeys, columnKey)
    saveVenueSuppliersHiddenColumns(venueId, next)
    setHiddenColumnKeys(next)
  }

  const handleTabChange = useCallback(
    key => {
      setActiveTab(key)
      if (!venueId) return
      try {
        localStorage.setItem(venueSuppliersTabKey(venueId), key)
      } catch {
        // ignore storage errors
      }
    },
    [venueId],
  )

  const beginFirstRestrict = item => {
    const pick = pickAcceptedSuggestedItem(item)
    handleTabChange('existing')
    const venueSupplier = pickExistingSupplierForFirstRestrict({
      item: pick,
      searchQuery: searchTerm,
      venueSuppliers,
      suggestedItems: omitInternalOrFixtureSuggestedItems(
        guidedSuccess?.suggestedData?.items,
      ),
    })
    if (!venueSupplier) return
    setFirstTryRestricted(true)
    setFirstTryApprovalEmails(
      defaultFirstTryApprovalEmails(venueSupplier, venue),
    )
    setFirstTryRestrictSupplier(venueSupplier)
  }
  const closeFirstTryRestrict = () => {
    if (firstTrySaving) return
    setFirstTryRestrictSupplier(null)
  }
  const saveFirstTryRestrict = async () => {
    if (!firstTryRestrictSupplier || firstTrySaving) return
    if (!firstTryRestricted) {
      closeFirstTryRestrict()
      return
    }
    if (
      !Array.isArray(firstTryApprovalEmails) ||
      firstTryApprovalEmails.length === 0
    ) {
      Modal.error({
        title: 'Approval emails required',
        content:
          'Please add at least one approval email address before enabling Restricted.',
        okText: 'OK',
      })
      return
    }
    const currentPlan = usageLimits?.currentPlan
    const restrictedLimit =
      usageLimits?.features?.Restricted_Suppliers?.limit ?? 1
    const otherRestrictedCount = (venueSuppliers || []).filter(
      vs => vs._id !== firstTryRestrictSupplier._id && vs.restricted === true,
    ).length
    if (currentPlan === 'Free' && otherRestrictedCount + 1 > restrictedLimit) {
      showRestrictedUpgradeModal({
        supplierTitle: firstTryRestrictSupplier.supplierTitle,
      })
      return
    }
    const payload = buildFirstTryRestrictSavePayload(firstTryRestrictSupplier, {
      restricted: true,
      approvalEmails: firstTryApprovalEmails,
    })
    if (!payload) return
    setFirstTrySaving(true)
    try {
      const updatedVenueSupplier = await dispatch(editVenueSupplier(payload))
      if (updatedVenueSupplier) {
        await dispatch(getVenueSuppliers(venueId))
        setFirstTryRestrictSupplier(null)
      } else {
        message.error('Failed to save supplier settings. Please try again.')
      }
    } finally {
      setFirstTrySaving(false)
    }
  }
  const suggestedEmpty = bindGuidedEmptySuggestedActions({
    venueId,
    featureId: 'restricted_suppliers',
    suggested,
    onStart: beginFirstRestrict,
  })

  // Load pending invites
  useEffect(() => {
    if (!venueId || !canManageSuppliers) return

    const loadInvites = async () => {
      try {
        const invites = await getSupplierInvites(venueId)
        setPendingInvites(invites)
      } catch (error) {
        console.error('Error loading invites:', error)
      }
    }

    loadInvites()
  }, [venueId, venueSuppliers, canManageSuppliers])

  // Refresh invites when modals close (after successful invite)
  const refreshInvites = async () => {
    if (venueId) {
      try {
        const invites = await getSupplierInvites(venueId)
        setPendingInvites(invites)
      } catch (error) {
        console.error('Error refreshing invites:', error)
      }
    }
  }

  const openConnectWizard = () => {
    setPreConnectSupplierCount(supplierConnectionCount)
    setShowConnectWizard(true)
  }

  const handleWizardFlowComplete = () => {
    if (venueId) {
      dispatch(getVenueSuppliers(venueId))
      setTimeout(() => {
        refreshInvites()
      }, 500)
    }
  }

  const handleWizardAddMore = () => {
    setShowConnectWizard(false)
    handleTabChange('connect')
    if (venueId) {
      try {
        localStorage.setItem(venueSuppliersTabKey(venueId), 'connect')
      } catch {
        // ignore storage errors
      }
    }
    setTimeout(() => {
      openConnectWizard()
    }, 0)
  }

  const handleInviteSupplier = supplierName => {
    setInviteSupplierName(supplierName || '')
    setShowInviteModal(true)
  }

  const handleInviteSuccess = () => {
    setInviteSupplierName('')
    refreshInvites() // Refresh the invites table
  }

  const handleRemoveInvite = async (inviteId, supplierName) => {
    if (!venueId || completingInviteId) return

    setCompletingInviteId(inviteId)
    try {
      const success = await removeSupplierInvite(venueId, inviteId)
      if (success) {
        showSuccessToast(
          supplierName
            ? `${supplierName} marked as added — thanks.`
            : 'Supplier marked as added — thanks.',
        )
        refreshInvites()
      } else {
        showErrorToast('Could not update this invite. Please try again.')
      }
    } catch {
      showErrorToast('Could not update this invite. Please try again.')
    } finally {
      setCompletingInviteId(null)
    }
  }

  const resetPagination = useCallback(() => {}, [])

  useLunrSearch(
    venueSuppliers,
    [
      'supplierTitle',
      'accountNumber',
      'minOrderValue',
      'deliveryDays',
      'deliveryFee',
    ],
    searchTerm,
    setSearchVenueSuppliers,
    resetPagination,
  )

  const supplierNameRedirect = record => {
    if (isSomeVenueSupplierActive) {
      dispatch(setCatalogueFilter(record.supplierTitle))
      history.push(linkPrefix)
    }
  }
  const content = (
    <div>
      <p style={{ maxWidth: 300 }}>
        Some suppliers need a few days notice before becoming available for
        orders, if you would like to request access to more suppliers please let
        us know at info@ordersmadesimple.com
      </p>
    </div>
  )

  const dataSource = isAdmin
    ? searchVenueSuppliers.map(venueSupplier => ({
        ...venueSupplier,
        key: venueSupplier._id,
        phoneNumber: venueSupplier?.supplier?.phoneNumber || '',
        secondTitle: venueSupplier?.supplier?.secondTitle || '',
      }))
    : isTfrAdmin
    ? searchVenueSuppliers
        .filter(
          venueSupplier =>
            venueSupplier.isActive || venueSupplier?.supplier?.TFRSupplier,
        )
        .map(venueSupplier => ({
          ...venueSupplier,
          key: venueSupplier._id,
          phoneNumber: venueSupplier?.supplier?.phoneNumber || '',
          secondTitle: venueSupplier?.supplier?.secondTitle || '',
        }))
    : isJustAdmin
    ? searchVenueSuppliers
        .filter(
          venueSupplier =>
            venueSupplier.isActive || venueSupplier?.supplier?.JUSTSupplier,
        )
        .map(venueSupplier => ({
          ...venueSupplier,
          key: venueSupplier._id,
          phoneNumber: venueSupplier?.supplier?.phoneNumber || '',
          secondTitle: venueSupplier?.supplier?.secondTitle || '',
        }))
    : isGreenAdmin
    ? searchVenueSuppliers
        .filter(
          venueSupplier =>
            venueSupplier.isActive || venueSupplier?.supplier?.GREENSupplier,
        )
        .map(venueSupplier => ({
          ...venueSupplier,
          key: venueSupplier._id,
          phoneNumber: venueSupplier?.supplier?.phoneNumber || '',
          secondTitle: venueSupplier?.supplier?.secondTitle || '',
        }))
    : searchVenueSuppliers
        .filter(venueSupplier => venueSupplier.isActive)
        .map(venueSupplier => ({
          ...venueSupplier,
          key: venueSupplier._id,
          phoneNumber: venueSupplier?.supplier?.phoneNumber || '',
          secondTitle: venueSupplier?.supplier?.secondTitle || '',
        }))

  const visibleDataSource = dataSource

  const isOutdated = useCallback(record => {
    if (record.nonOMSSupplier) return false
    const lastUpdate =
      record.supplier?.last_price_update_at ??
      record.supplier?.lastPriceUpdateFromProducts
    if (!lastUpdate) return false
    return dayjs().diff(dayjs(lastUpdate), 'day') >= 60
  }, [])

  const outdatedCount = useMemo(
    () => visibleDataSource.filter(isOutdated).length,
    [visibleDataSource, isOutdated],
  )

  const scrollToFirstOutdated = useCallback(() => {
    const el = document.querySelector('.outdated-supplier-row')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  const trackPriceStatusEvent = useCallback(eventName => {
    try {
      if (typeof window !== 'undefined' && window.analytics?.track) {
        window.analytics.track(eventName)
      }
    } catch (_) {
      // Analytics not available; ignore
    }
  }, [])

  const handleRequestRefreshClick = useCallback(
    (record, e) => {
      e.stopPropagation()
      const supplierName = record.supplierTitle || 'Supplier'
      const supplierEmail =
        record.supplier?.emailAddress || '(no email on file)'
      const supplierId = record.supplier?._id ?? record.supplier
      Modal.confirm({
        title: 'Request price refresh',
        content: (
          <span>
            We&apos;ll send an email to <strong>{supplierName}</strong> at{' '}
            <strong>{supplierEmail}</strong> asking them to update their price
            file in the OMS Supplier Portal. Do you want to continue?
          </span>
        ),
        okText: 'Send request',
        cancelText: 'Cancel',
        onOk: async () => {
          const success = await dispatch(
            requestPriceRefresh(venueId, supplierId),
          )
          if (success) {
            message.success(`Request sent to ${supplierName}`)
            trackPriceStatusEvent('price_refresh_requested')
          } else {
            message.error(
              'Could not send request. You may be in a 24-hour cooldown or the supplier has no email on file.',
            )
          }
        },
        onCancel: () => {
          trackPriceStatusEvent('price_refresh_request_modal_dismissed')
        },
      })
    },
    [dispatch, venueId, trackPriceStatusEvent],
  )

  return (
    <Wrapper
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
      isSomeVenueSupplierActive={isSomeVenueSupplierActive}
    >
      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <TitleRow>
              <FoodSafetyPageTitle>Your suppliers</FoodSafetyPageTitle>
              <ShareWithGroupButton
                offer={{ kind: 'restrictedSuppliers', venueId }}
                label='Share restricted suppliers…'
              />
              <Popover
                placement='right'
                title='Want more suppliers?'
                content={content}
              >
                <InfoCircleOutlined style={{ marginTop: 6 }} />
              </Popover>
            </TitleRow>
            <HeroIntro>Browse your suppliers catalogues</HeroIntro>
          </div>
        </FoodSafetyHeaderRow>
        {activeTab === 'existing' ? (
          <>
            <HeroLead>
              Click a supplier to see their whole catalogue and add products to
              your shopping lists.
            </HeroLead>
            {hideFirstTryMarketing || !canManageSuppliers ? null : (
              <HeroHelp>
                <p>
                  Stay in control of your supplier settings and ordering
                  visibility.
                </p>
                <strong>This tool helps you:</strong>
                <ul>
                  <li>
                    Click &apos;Edit&apos; to map which account and contact
                    orders go to on your accounting platform
                  </li>
                  <li>
                    Click &apos;Edit&apos; to enable or disable &apos;Show
                    Catalogue&apos; — control whether staff can browse full
                    supplier catalogues on the app, or only see Shopping List
                    products
                  </li>
                </ul>
              </HeroHelp>
            )}
          </>
        ) : null}
      </FoodSafetyHeroBand>

      <SupplierProgress
        currentPlan={currentPlan}
        activeSupplierCount={growthEngineActiveSupplierCount}
      />

      {showFirstSuccessCompletion ? (
        <GrowthEngineFirstSuccessCompletion
          outcome={firstSuccessCompletion.outcome}
          nextSummary={firstSuccessCompletion.nextSummary}
          ctaLabel={firstSuccessCompletion.ctaLabel}
          managerOnePager={firstSuccessCompletion.managerOnePager}
          featureId='restricted_suppliers'
          onCta={() => {
            setJustAchievedFirstRestrict(false)
          }}
          onProgress={
            venueId ? () => history.push(`/v/${venueId}/progress`) : undefined
          }
        />
      ) : null}

      {showRestrictedGuidedEmpty ? (
        <GrowthEngineFeatureEmptyState
          featureId='restricted_suppliers'
          title={guidedSuccess.emptyState.title}
          body={guidedSuccess.emptyState.body}
          valueLine={guidedSuccess.emptyState.valueLine}
          estimatedMinutes={guidedSuccess.emptyState.estimatedMinutes}
          ctaLabel={guidedSuccess.emptyState.cta?.label}
          suggestedLabel='Start with a supplier you already use'
          onCta={suggestedEmpty.onCta}
          suggestedItems={suggestedEmpty.suggestedItems}
          onSuggestedItem={suggestedEmpty.onSuggestedItem}
          onSuggestedEdit={suggestedEmpty.onSuggestedEdit}
          onRejectSuggested={suggestedEmpty.onRejectSuggested}
        />
      ) : null}

      <StyledTabs activeKey={activeTab} onChange={handleTabChange} type='card'>
        {canManageSuppliers && (
          <Tabs.TabPane tab='Add supplier' key='connect'>
            <TabContent>
              <ConnectHeader>
                <ConnectTitle>Add suppliers to your OMS account</ConnectTitle>
                <ConnectSubtitle>
                  Find suppliers already on OMS or invite new ones to join.
                </ConnectSubtitle>
              </ConnectHeader>

              <AddSupplierHub
                onFindOnOms={openConnectWizard}
                onInvite={() => handleInviteSupplier('')}
              />

              <HelperText>
                Next: Add products from the Catalogue tab to your shopping
                lists.
              </HelperText>

              <InvitesSection>
                <InvitesTitle>Pending invites</InvitesTitle>
                {pendingInvites.length === 0 ? (
                  <EmptyState>
                    <EmptyStateText>
                      No pending invites. Invite a supplier above to track
                      progress here.
                    </EmptyStateText>
                  </EmptyState>
                ) : (
                  <Table
                    dataSource={pendingInvites}
                    rowKey='id'
                    pagination={false}
                    size='small'
                    columns={[
                      {
                        title: 'Supplier Name',
                        dataIndex: 'supplierName',
                        key: 'supplierName',
                      },
                      {
                        title: 'Email',
                        dataIndex: 'supplierEmail',
                        key: 'supplierEmail',
                      },
                      {
                        title: 'Account Number',
                        dataIndex: 'accountNumber',
                        key: 'accountNumber',
                        render: text => text || '-',
                      },
                      {
                        title: 'Date/Time Requested',
                        dataIndex: 'requestedAt',
                        key: 'requestedAt',
                        render: date => formatInviteDate(date),
                      },
                      {
                        title: 'Supplier Added',
                        key: 'added',
                        render: (_, record) => {
                          const isCompleting = completingInviteId === record.id
                          return (
                            <InviteCompleteCell>
                              {isCompleting ? (
                                <Spin size='small' />
                              ) : (
                                <Checkbox
                                  disabled={Boolean(completingInviteId)}
                                  onChange={() =>
                                    handleRemoveInvite(
                                      record.id,
                                      record.supplierName,
                                    )
                                  }
                                />
                              )}
                            </InviteCompleteCell>
                          )
                        },
                      },
                    ]}
                  />
                )}
              </InvitesSection>
            </TabContent>
          </Tabs.TabPane>
        )}

        <Tabs.TabPane tab='Existing Suppliers' key='existing'>
          <TabContent>
            <SearchWrapper>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                {!hideFirstTryMarketing && !isLoading && outdatedCount > 0 && (
                  <Tooltip title='Scroll to first outdated supplier'>
                    <AlertBellButton
                      type='button'
                      onClick={e => {
                        e.stopPropagation()
                        scrollToFirstOutdated()
                      }}
                      aria-label={`${outdatedCount} suppliers may have outdated pricing`}
                    >
                      <Badge
                        count={outdatedCount}
                        size='small'
                        offset={[-2, 2]}
                      >
                        <BellOutlined
                          style={{ color: '#f5222d', fontSize: 18 }}
                        />
                      </Badge>
                      <span style={{ marginLeft: 8, fontSize: 13 }}>
                        {outdatedCount} supplier{outdatedCount !== 1 ? 's' : ''}{' '}
                        haven&apos;t updated their price files in 60+ days – ask
                        them to update in the Supplier Portal
                      </span>
                    </AlertBellButton>
                  </Tooltip>
                )}
                <Search
                  size='large'
                  placeholder='Search'
                  onSearch={value => setSearchTerm(value)}
                  style={{ width: 300 }}
                  allowClear
                />
                <Button
                  data-testid='venue-suppliers-columns-button'
                  onClick={() => setColumnPickerOpen(true)}
                >
                  <TableOutlined /> Edit columns
                </Button>
              </div>
            </SearchWrapper>

            <Modal
              title='Edit columns'
              visible={columnPickerOpen}
              onCancel={() => setColumnPickerOpen(false)}
              onOk={() => setColumnPickerOpen(false)}
              okText='Done'
              cancelButtonProps={{ style: { display: 'none' } }}
              data-testid='venue-suppliers-columns-modal'
            >
              <p style={{ marginTop: 0 }}>
                Title and Edit always stay visible. Tick the others you want on
                this supplier table.
              </p>
              <ColumnPickerList>
                {suppliersPickerRows.map(column => (
                  <Checkbox
                    key={column.key}
                    checked={!pickerHiddenKeys.includes(column.key)}
                    onChange={() => handleToggleSuppliersColumn(column.key)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </ColumnPickerList>
            </Modal>

            {isLoading ? (
              <Loading />
            ) : (
              <Fragment>
                <Table
                  onRow={record => {
                    return {
                      onClick: () => {
                        supplierNameRedirect(record)
                      },
                      className: isOutdated(record)
                        ? 'outdated-supplier-row'
                        : '',
                    }
                  }}
                  scroll={RESPONSIVE_TABLE_SCROLL}
                  dataSource={visibleDataSource}
                  pagination={{
                    position: 'both',
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                  }}
                  columns={filterVenueSuppliersVisibleColumns(
                    [
                      {
                        title: 'Title',
                        dataIndex: 'supplierTitle',
                        key: 'supplierTitle',
                        className: 'restrictShrink',
                        sorter: (a, b) =>
                          String(a.supplierTitle)
                            .trim()
                            .localeCompare(String(b.supplierTitle).trim()),
                        sortDirections: ['ascend', 'descend'],
                        defaultSortOrder: 'ascend',
                        render: (title, record) => {
                          const supplierId = String(
                            record.supplier?._id || record.supplier || '',
                          )
                          return (
                            <span>
                              {title}
                              {sharedPriceFileIds.has(supplierId) ? (
                                <ShareWithGroupButton
                                  offer={{
                                    kind: 'priceFiles',
                                    venueId,
                                    supplierId,
                                  }}
                                  label='Share price file…'
                                />
                              ) : null}
                              {supplierId ? (
                                <ShareWithGroupButton
                                  offer={{
                                    kind: 'connectSupplier',
                                    venueId,
                                    supplierId,
                                  }}
                                  label='Turn on for group…'
                                />
                              ) : null}
                            </span>
                          )
                        },
                      },

                      isAdmin || isTfrAdmin || isJustAdmin || isGreenAdmin
                        ? {
                            title: 'Second title',
                            dataIndex: 'secondTitle',
                            key: 'secondTitle',
                            className: 'restrictShrink',
                            sorter: (a, b) =>
                              String(a.secondTitle)
                                .trim()
                                .localeCompare(String(b.secondTitle).trim()),
                            sortDirections: ['ascend', 'descend'],
                            defaultSortOrder: 'ascend',
                          }
                        : {},

                      {
                        title: 'Account number',
                        dataIndex: 'accountNumber',
                        key: 'accountNumber',
                        className: 'restrictShrink',
                      },

                      {
                        title: 'Minimum order',
                        dataIndex: 'minOrderValue',
                        key: 'minOrderValue',
                        className: 'restrictShrink',
                        render: text => <span>£{text / 100}</span>,
                        sorter: (a, b) => a.minOrderValue - b.minOrderValue,
                        sortDirections: ['ascend', 'descend'],
                      },

                      {
                        title: 'Delivery Fee Below Minimum (ex VAT)',
                        dataIndex: 'deliveryFee',
                        key: 'deliveryFee',
                        className: 'restrictShrink',
                        render: text => {
                          return (
                            <span>£{(Number(text || 0) / 100).toFixed(2)}</span>
                          )
                        },
                      },

                      {
                        title: 'Delivery days',
                        dataIndex: 'deliveryDays',
                        key: 'deliveryDays',
                        className: 'restrictShrink',
                        render: text => {
                          return (
                            <span>{formatDeliveryDays(text).join(', ')}</span>
                          )
                        },
                      },

                      ...(accountCreditApproved && isAdmin
                        ? [
                            {
                              title: 'Credit status',
                              dataIndex: 'allowsCreditPurchase',
                              key: 'allowsCreditPurchase',
                              className: 'restrictShrink',
                              render: (text, venueSupplier) => {
                                const supplierId = venueSupplier.supplier
                                const supplier = suppliers.find(
                                  supplier => supplier._id === supplierId,
                                )
                                return (
                                  <Tag
                                    color={
                                      supplier?.allowsCreditPurchase
                                        ? 'green'
                                        : 'orange'
                                    }
                                  >
                                    {supplier?.allowsCreditPurchase
                                      ? 'Credit terms'
                                      : 'Pay on order'}
                                  </Tag>
                                )
                              },
                            },
                          ]
                        : []),

                      {
                        title: ORDER_CUTOFF_TIME_LABEL,
                        key: 'supplierOrderCutOffTime',
                        className: 'restrictShrink',
                        render: (_text, venueSupplier) => {
                          const supplierCutOffTime = String(
                            venueSupplier?.supplier?.orderCutOffTime ?? '',
                          ).trim()

                          return <span>{supplierCutOffTime || 'Not set'}</span>
                        },
                      },
                      {
                        title: ORDER_CUTOFF_INSTRUCTIONS_LABEL,
                        dataIndex: 'orderCutOffTime',
                        key: 'orderCutOffInstructions',
                        className: 'restrictShrink',
                        render: venueInstructions => (
                          <span>
                            {resolveOrderCutOffInstructionsDisplay(
                              venueInstructions,
                            )}
                          </span>
                        ),
                      },

                      {
                        title: (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            Price File Status
                            <Popover
                              placement='topLeft'
                              title='How supplier pricing stays updated'
                              content={
                                <div style={{ maxWidth: 360 }}>
                                  <p style={{ marginBottom: 8 }}>
                                    <strong>
                                      How you see up-to-date prices:
                                    </strong>
                                  </p>
                                  <ul
                                    style={{ marginBottom: 8, paddingLeft: 18 }}
                                  >
                                    <li>
                                      When a supplier{' '}
                                      <strong>integrates</strong> with OMS,
                                      their prices can flow automatically.
                                    </li>
                                    <li>
                                      When they{' '}
                                      <strong>don&apos;t integrate</strong>,
                                      they must update their price file in the{' '}
                                      <strong>OMS Supplier Portal</strong>. We
                                      offer integration for free; some still
                                      choose to update manually.
                                    </li>
                                    <li>
                                      We also update prices when{' '}
                                      <strong>you upload invoices</strong> and
                                      we extract line items.
                                    </li>
                                  </ul>
                                  <p style={{ marginBottom: 8 }}>
                                    <strong>
                                      If a supplier is showing as outdated:
                                    </strong>{' '}
                                    Ask them to log into the{' '}
                                    <strong>OMS Supplier Portal</strong> and
                                    upload a new price file.{' '}
                                    <strong>
                                      All OMS suppliers have access
                                    </strong>{' '}
                                    – if they say they don&apos;t, ask them to
                                    contact us.
                                  </p>
                                  <p style={{ marginBottom: 0 }}>
                                    <strong>Caveat:</strong> Some suppliers
                                    don&apos;t change prices often, so
                                    &quot;outdated&quot; here means no file
                                    update in 60+ days, not necessarily wrong
                                    prices. When in doubt, check with the
                                    supplier.
                                  </p>
                                </div>
                              }
                              trigger='hover'
                            >
                              <span
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                                style={{
                                  cursor: 'pointer',
                                  color: '#1890ff',
                                  display: 'inline-flex',
                                }}
                                role='button'
                                tabIndex={0}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation()
                                  }
                                }}
                                aria-label='More info about Price File Status'
                              >
                                <InfoCircleOutlined />
                              </span>
                            </Popover>
                          </span>
                        ),
                        key: 'priceFileStatus',
                        className: 'restrictShrink',
                        sorter: (a, b) => {
                          const lastUpdateA =
                            a.supplier?.last_price_update_at ??
                            a.supplier?.lastPriceUpdateFromProducts
                          const lastUpdateB =
                            b.supplier?.last_price_update_at ??
                            b.supplier?.lastPriceUpdateFromProducts
                          const tsA = lastUpdateA
                            ? new Date(lastUpdateA).getTime()
                            : 0
                          const tsB = lastUpdateB
                            ? new Date(lastUpdateB).getTime()
                            : 0
                          return tsA - tsB
                        },
                        sortDirections: ['ascend', 'descend'],
                        render: (_, record) => {
                          const priceStatusTooltip =
                            "Pricing is updated by suppliers – either via their integration with OMS (automatic) or by uploading price files in the OMS Supplier Portal. If this supplier hasn't updated in a while, ask them to log into the Supplier Portal and upload a new price file. All OMS suppliers have access."
                          const lastUpdate =
                            record.supplier?.last_price_update_at ??
                            record.supplier?.lastPriceUpdateFromProducts
                          const showRequestRefresh =
                            canManageSuppliers &&
                            !record.nonOMSSupplier &&
                            lastUpdate &&
                            dayjs().diff(dayjs(lastUpdate), 'day') >= 30
                          // Managers already get the report control in sticky actions.
                          const showReportPriceFileIssue =
                            canReportPriceFileIssue &&
                            !canManageSuppliers &&
                            !record.nonOMSSupplier
                          const lastRequestedAt =
                            record.lastPriceRefreshRequestedAt
                          const cooldownHours = lastRequestedAt
                            ? dayjs().diff(dayjs(lastRequestedAt), 'hour')
                            : 24
                          const isInCooldown =
                            lastRequestedAt && cooldownHours < 24

                          let statusNode = (
                            <Tooltip title={priceStatusTooltip}>
                              <span style={{ color: '#999' }}>No data yet</span>
                            </Tooltip>
                          )

                          if (lastUpdate) {
                            const updatedAt = dayjs(lastUpdate)
                            const daysAgo = dayjs().diff(updatedAt, 'day')
                            let color = '#999'
                            let text
                            if (daysAgo < 30) {
                              color = '#52c41a'
                              text = `Updated ${updatedAt.format('D MMM')}`
                            } else if (daysAgo < 60) {
                              color = '#fa8c16'
                              text = `Updated ${daysAgo} days ago`
                            } else {
                              color = '#f5222d'
                              text = `Updated ${daysAgo} days ago`
                            }
                            statusNode = (
                              <Tooltip title={priceStatusTooltip}>
                                <span style={{ color }}>{text}</span>
                              </Tooltip>
                            )
                          }

                          return (
                            <div
                              onClick={e => e.stopPropagation()}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                              }}
                            >
                              {statusNode}
                              {showRequestRefresh && (
                                <span style={{ fontSize: 12 }}>
                                  {isInCooldown ? (
                                    <span style={{ color: '#999' }}>
                                      Request sent {cooldownHours}h ago
                                    </span>
                                  ) : (
                                    <Button
                                      type='link'
                                      size='small'
                                      style={{
                                        padding: 0,
                                        height: 'auto',
                                        fontSize: 12,
                                      }}
                                      onClick={e =>
                                        handleRequestRefreshClick(record, e)
                                      }
                                    >
                                      Request refresh
                                    </Button>
                                  )}
                                </span>
                              )}
                              {showReportPriceFileIssue && (
                                <ReportPriceFileIssueButton
                                  venueId={venueId}
                                  venueSupplier={record}
                                  type='link'
                                  size='small'
                                  style={{
                                    padding: 0,
                                    height: 'auto',
                                    fontSize: 12,
                                    justifyContent: 'flex-start',
                                  }}
                                />
                              )}
                            </div>
                          )
                        },
                      },

                      {
                        title: 'Phone number',
                        dataIndex: 'phoneNumber',
                        key: 'phoneNumber',
                        className: 'restrictShrink',
                      },

                      ...(canManageSuppliers
                        ? [
                            isAdmin || isTfrAdmin || isJustAdmin || isGreenAdmin
                              ? {
                                  title: 'Supplier in app',
                                  key: 'actions',
                                  className:
                                    'restrictShrink sticky-actions-col',
                                  onCell: () => ({
                                    className: 'sticky-actions-cell',
                                  }),
                                  fixed: 'right',
                                  width: 220,
                                  render: (text, venueSupplier) => (
                                    <ButtonsCell>
                                      <Icon
                                        onClick={event => {
                                          event.preventDefault()
                                          event.stopPropagation()
                                        }}
                                      >
                                        {venueSupplier.isActive ? (
                                          <CheckOutlined
                                            style={{ color: 'green' }}
                                          />
                                        ) : (
                                          <CloseOutlined
                                            style={{ color: 'red' }}
                                          />
                                        )}
                                      </Icon>
                                      <Button
                                        type={'dashed'}
                                        onClick={event => {
                                          setEditingVenueSupplier(venueSupplier)
                                          event.stopPropagation()
                                        }}
                                      >
                                        <span>Edit</span>
                                      </Button>
                                      {canReportPriceFileIssue &&
                                        !venueSupplier.nonOMSSupplier && (
                                          <div
                                            onClick={e => e.stopPropagation()}
                                            style={{ flexBasis: '100%' }}
                                          >
                                            <ReportPriceFileIssueButton
                                              venueId={venueId}
                                              venueSupplier={venueSupplier}
                                              type='dashed'
                                              size='small'
                                            />
                                          </div>
                                        )}
                                    </ButtonsCell>
                                  ),
                                  sorter: (a, b) => a.isActive - b.isActive,
                                  sortDirections: ['descend', 'ascend'],
                                }
                              : {
                                  title: 'Edit',
                                  key: 'actions',
                                  className:
                                    'restrictShrink sticky-actions-col',
                                  onCell: () => ({
                                    className: 'sticky-actions-cell',
                                  }),
                                  fixed: 'right',
                                  width: 180,
                                  render: (text, venueSupplier) => (
                                    <ButtonsCell>
                                      <Button
                                        type={'dashed'}
                                        onClick={event => {
                                          setEditingVenueSupplier(venueSupplier)
                                          event.stopPropagation()
                                        }}
                                      >
                                        <span>Edit</span>
                                      </Button>
                                      {canReportPriceFileIssue &&
                                        !venueSupplier.nonOMSSupplier && (
                                          <div
                                            onClick={e => e.stopPropagation()}
                                            style={{ flexBasis: '100%' }}
                                          >
                                            <ReportPriceFileIssueButton
                                              venueId={venueId}
                                              venueSupplier={venueSupplier}
                                              type='dashed'
                                              size='small'
                                            />
                                          </div>
                                        )}
                                    </ButtonsCell>
                                  ),
                                },
                          ]
                        : []),
                    ],
                    {
                      hiddenKeys: hiddenColumnKeys,
                    },
                  )}
                />

                {canManageSuppliers && (
                  <Modal
                    title='Edit Supplier'
                    visible={editingVenueSupplier !== null}
                    destroyOnClose
                    confirmLoading={editingSupplierSaving}
                    okButtonProps={{ disabled: editingSupplierSaving }}
                    cancelButtonProps={{ disabled: editingSupplierSaving }}
                    onOk={async () => {
                      if (!editingVenueSupplier || editingSupplierSaving) {
                        return
                      }

                      const currentPlan = usageLimits?.currentPlan
                      const restrictedLimit =
                        usageLimits?.features?.Restricted_Suppliers?.limit ?? 1

                      const otherRestrictedCount = venueSuppliers.filter(
                        vs =>
                          vs._id !== editingVenueSupplier._id &&
                          vs.restricted === true,
                      ).length

                      const resultingRestrictedCount =
                        otherRestrictedCount +
                        (editingVenueSupplier.restricted ? 1 : 0)

                      if (
                        editingVenueSupplier.restricted &&
                        (!Array.isArray(editingVenueSupplier.approvalEmails) ||
                          editingVenueSupplier.approvalEmails.length === 0)
                      ) {
                        Modal.error({
                          title: 'Approval emails required',
                          content:
                            'Please add at least one approval email address before enabling Restricted Supplier.',
                          okText: 'OK',
                        })
                        return
                      }

                      if (
                        editingVenueSupplier.restricted &&
                        currentPlan === 'Free' &&
                        resultingRestrictedCount > restrictedLimit
                      ) {
                        showRestrictedUpgradeModal({
                          supplierTitle: editingVenueSupplier.supplierTitle,
                        })
                        return
                      }

                      setEditingSupplierSaving(true)
                      try {
                        const { pendingPriceFileId, ...venueSupplierToSave } =
                          editingVenueSupplier

                        const currentPriceFileId = String(
                          venueSupplierToSave.supplier?._id ||
                            venueSupplierToSave.supplier ||
                            '',
                        )

                        let workingVenueSupplier = venueSupplierToSave

                        if (
                          pendingPriceFileId &&
                          String(pendingPriceFileId) !== currentPriceFileId
                        ) {
                          const switched = await dispatch(
                            switchVenueSupplierPriceFile({
                              venueId,
                              venueSupplierId: venueSupplierToSave._id,
                              priceFileId: pendingPriceFileId,
                            }),
                          )
                          if (!switched || switched.success === false) {
                            message.error(
                              switched?.message ||
                                'Failed to switch price file. Please try again.',
                            )
                            return
                          }

                          const switchedVs = switched.venueSupplier
                          workingVenueSupplier = {
                            ...switchedVs,
                            accountNumber: venueSupplierToSave.accountNumber,
                            sageSupplierAccountNumber:
                              venueSupplierToSave.sageSupplierAccountNumber,
                            minOrderValue: venueSupplierToSave.minOrderValue,
                            deliveryDays: venueSupplierToSave.deliveryDays,
                            orderCutOffTime:
                              venueSupplierToSave.orderCutOffTime,
                            isActive: venueSupplierToSave.isActive,
                            isCatalogueAccessible:
                              venueSupplierToSave.isCatalogueAccessible,
                            manualOrdersOnly:
                              venueSupplierToSave.manualOrdersOnly,
                            restricted: venueSupplierToSave.restricted,
                            approvalEmails: venueSupplierToSave.approvalEmails,
                            requireGoodsReceiptBeforeReorder:
                              venueSupplierToSave.requireGoodsReceiptBeforeReorder ===
                              true,
                            defaultAccount: venueSupplierToSave.defaultAccount,
                            defaultContact: venueSupplierToSave.defaultContact,
                            defaultContactDetails:
                              venueSupplierToSave.defaultContactDetails,
                          }
                        }

                        const updatedVenueSupplier = await dispatch(
                          editVenueSupplier({
                            ...workingVenueSupplier,
                            // Always persist an explicit boolean (default OFF).
                            requireGoodsReceiptBeforeReorder:
                              workingVenueSupplier.requireGoodsReceiptBeforeReorder ===
                              true,
                          }),
                        )
                        if (updatedVenueSupplier) {
                          await dispatch(getVenueSuppliers(venueId))
                          showSuccessToast(
                            supplierSaveConfirmation({
                              before: mappingAtOpenRef.current,
                              after: workingVenueSupplier,
                            }),
                          )
                          setEditingVenueSupplier(null)
                        } else {
                          message.error(
                            'Failed to save supplier settings. Please try again.',
                          )
                        }
                      } finally {
                        setEditingSupplierSaving(false)
                      }
                    }}
                    onCancel={() => {
                      if (editingSupplierSaving) {
                        return
                      }
                      setEditingVenueSupplier(null)
                    }}
                  >
                    {supplierModalEntity && (
                      <EditVenueSupplierModal
                        venueSupplier={supplierModalEntity}
                        account={account}
                        venueId={venueId}
                        setEditingVenueSupplier={setEditingVenueSupplier}
                        onShowUpgradeModal={showRestrictedUpgradeModal}
                      />
                    )}
                  </Modal>
                )}
              </Fragment>
            )}
          </TabContent>
        </Tabs.TabPane>
      </StyledTabs>

      {canManageSuppliers && (
        <>
          <ConnectSupplierWizard
            visible={showConnectWizard}
            onClose={() => setShowConnectWizard(false)}
            venueId={venueId}
            venueSuppliers={venueSuppliers}
            preConnectSupplierCount={preConnectSupplierCount}
            onFlowComplete={handleWizardFlowComplete}
            onAddMore={handleWizardAddMore}
            onInviteSupplier={handleInviteSupplier}
          />

          <InviteSupplierModal
            visible={showInviteModal}
            onCancel={() => {
              setShowInviteModal(false)
              setInviteSupplierName('')
            }}
            supplierName={inviteSupplierName}
            onSuccess={handleInviteSuccess}
          />
        </>
      )}

      {canManageSuppliers && firstTryRestrictSupplier ? (
        <Modal
          title='Restrict a supplier'
          visible
          confirmLoading={firstTrySaving}
          okText={firstTryRestricted ? 'Restrict this supplier' : 'Close'}
          cancelText='Not now'
          onOk={saveFirstTryRestrict}
          onCancel={closeFirstTryRestrict}
        >
          <RestrictSupplierFirstTrySheet
            venueSupplier={firstTryRestrictSupplier}
            restricted={firstTryRestricted}
            approvalEmails={firstTryApprovalEmails}
            onRestrictedChange={setFirstTryRestricted}
            onApprovalEmailsChange={setFirstTryApprovalEmails}
          />
        </Modal>
      ) : null}

      {showRestrictedLimitModal && (
        <PlanLimitModal
          showLimitModal={showRestrictedLimitModal}
          setShowLimitModal={setShowRestrictedLimitModal}
          variant='restrictedSupplier'
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)([
  visibleHorizontalScroll,
  antdTableHorizontalScroll,
  stickyActionsColumn,
  css`
    min-width: 0;
    max-width: 100%;

    .selected {
      background: rgba(180, 216, 32, 0.33);
    }
    .restrictShrink {
      min-width: 95px;
    }
    .ant-table-row {
      cursor: ${props =>
        props.isSomeVenueSupplierActive ? 'pointer' : 'default'};
      &:hover {
        background-color: ${({ theme }) => theme.colours.bgGrey};
        > td:not(.sticky-actions-cell) {
          background: none !important;
        }
        > td.sticky-actions-cell {
          background-color: ${({ theme }) => theme.colours.bgGrey};
        }
      }
    }
    .outdated-supplier-row > td {
      background-color: #fff2f0;
    }
    .outdated-supplier-row:hover > td:not(.sticky-actions-cell) {
      background-color: #ffebe8 !important;
    }
    .outdated-supplier-row > td.sticky-actions-cell,
    .outdated-supplier-row:hover > td.sticky-actions-cell {
      background-color: #fff2f0;
    }
  `,
])

const ButtonsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
`

const ColumnPickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Icon = styled.span`
  display: flex;
  flex: 1;
  justify-content: center;
`

const AlertBellButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  cursor: pointer;
  color: #cf1322;
  font-size: 13px;
  transition: background 0.2s;
  &:hover {
    background: #ffebe8;
  }
`

const StyledTabs = styled(Tabs)`
  width: 100%;

  .ant-tabs-nav {
    margin-bottom: 0;
    padding: 0 20px;
    background: transparent;
    border-bottom: 1px solid #e8e8e8;
  }

  .ant-tabs-nav-wrap {
    justify-content: flex-start;
  }

  .ant-tabs-tab {
    font-size: 14px;
    font-weight: 500;
    padding: 12px 20px;
    margin-right: 0;
    border: none;
    border-radius: 6px 6px 0 0;
    background: transparent;
    color: #8c8c8c;
    transition: all 0.2s ease;

    &:hover {
      color: #52c41a;
      background: rgba(82, 196, 26, 0.08);
    }

    &.ant-tabs-tab-active {
      color: #52c41a;
      border-bottom: 2px solid #52c41a;
      background: #fff;
      box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
    }
  }

  .ant-tabs-content-holder {
    background: #fafafa;
    min-height: 500px;
  }

  .ant-tabs-tabpane {
    padding: 0;
  }
`

const TabContent = styled.div`
  width: 100%;
  padding: 32px 20px;
`

const ConnectHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`

const ConnectTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #262626;
`

const ConnectSubtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 0;
  line-height: 1.5;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin-top: 16px;
  text-align: center;
  line-height: 1.5;
`

const InvitesSection = styled.div`
  margin-top: 48px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`

const InvitesTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
`

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const HeroIntro = styled.p`
  margin: 8px 0 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.45;
`

const HeroLead = styled.p`
  margin: 16px 0 0;
  color: #132d07;
  font-weight: 600;
  line-height: 1.45;
`

const HeroHelp = styled.div`
  margin-top: 12px;
  color: #555;
  line-height: 1.45;

  p {
    margin: 0 0 8px;
  }

  ul {
    margin: 8px 0 0 18px;
  }
`

const SearchWrapper = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: flex-start;
`

const InviteCompleteCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
`

export default VenueSuppliers
